import { z } from 'zod';

import {
    type InlineReviewOutcome,
    type SmsEngineAnnotationSourceType,
} from '@libraries/typescript/api_client';

export const PER_PAGE = 50;

export const STATUS_OPTIONS = [
    'unreviewed',
    'needs_review',
    'good',
    'bad',
] as const;
export const MODE_OPTIONS = ['live', 'sandbox'] as const;
export const DOMAIN_TYPE_OPTIONS = [
    'shift_confirmation',
    'callout',
    'shifts',
] as const;
export const ACTION_TYPE_OPTIONS = [
    'shift_confirmation_response',
    'callout_response',
    'clock_in',
    'clock_out',
    'shift_cancellation',
    'shift_cancellation_clarification',
    'shift_cancellation_confirmation_request',
    'shift_cancellation_handoff',
    'no_response_needed',
    'agency_escalation',
    'caregiver_availability',
    'caregiver_memory',
] as const;
export const INLINE_REVIEW_OUTCOME_OPTIONS = [
    'pending',
    'accepted',
    'rewrite_applied',
    'rewrite_no_op',
    'rewrite_failed',
    'rewrite_skipped',
    'preview_accepted',
    'preview_would_rewrite',
    'preview_rewrite_failed',
    'preview_rewrite_no_op',
    'preview_would_disable_auto_send',
    'error',
] as const satisfies ReadonlyArray<InlineReviewOutcome>;
export const INLINE_REVIEW_VERDICT_OPTIONS = ['GOOD', 'BAD'] as const;
export const INLINE_REVIEW_MODE_OPTIONS = ['enforce', 'preview'] as const;
export const REVIEW_SOURCE_TYPE_OPTIONS = [
    'human',
    'user',
    'agent',
] as const satisfies ReadonlyArray<SmsEngineAnnotationSourceType>;
export const TALENT_SIGNAL_FILTER_OPTIONS = [true] as const;

const arrayParam = <T extends z.ZodTypeAny>(schema: T) =>
    z
        .preprocess((value) => {
            if (value === undefined || value === null) {
                return undefined;
            }
            if (Array.isArray(value)) {
                return value;
            }
            if (typeof value === 'string') {
                if (!value.trim()) {
                    return [];
                }
                return value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
            }
            return value;
        }, z.array(schema))
        .optional();

const booleanParam = z
    .preprocess((value) => {
        if (value === undefined || value === null || value === '') {
            return undefined;
        }
        const scalarValue = Array.isArray(value) ? value[0] : value;
        if (typeof scalarValue === 'string') {
            const normalized = scalarValue.trim().toLowerCase();
            if (normalized === 'true') {
                return true;
            }
            if (normalized === 'false') {
                return false;
            }
        }
        return scalarValue;
    }, z.boolean())
    .optional();

export const smsEngineRunsSearchSchema = z.object({
    page: z.coerce.number().optional().default(1),
    status: arrayParam(z.enum(STATUS_OPTIONS)),
    status_not: arrayParam(z.enum(STATUS_OPTIONS)),
    mode: arrayParam(z.enum(MODE_OPTIONS)),
    organization_name: arrayParam(z.string()),
    domain_type: arrayParam(z.enum(DOMAIN_TYPE_OPTIONS)),
    action_type: arrayParam(z.enum(ACTION_TYPE_OPTIONS)),
    inline_review_verdict: arrayParam(z.enum(INLINE_REVIEW_VERDICT_OPTIONS)),
    inline_review_mode: arrayParam(z.enum(INLINE_REVIEW_MODE_OPTIONS)),
    inline_review_outcome: arrayParam(z.enum(INLINE_REVIEW_OUTCOME_OPTIONS)),
    review_source_type: arrayParam(z.enum(REVIEW_SOURCE_TYPE_OPTIONS)),
    talent_signal: booleanParam,
    created_after: z.string().optional(),
    created_before: z.string().optional(),
    search: z.string().optional(),
    sort_by: z
        .enum(['created_at', 'organization_name', 'status'])
        .optional()
        .default('created_at')
        .catch('created_at'),
    sort_order: z.enum(['asc', 'desc']).optional().default('desc'),
});

export type SmsEngineRunsSearch = z.infer<typeof smsEngineRunsSearchSchema>;

/**
 * The detail view inherits every list filter/sort/page param so it can rebuild
 * the exact list it was opened from. `from_list` is set only when navigating
 * from the list; its absence (a shared link, bookmark, or refresh without the
 * params) is the signal that there is no list context to navigate.
 */
export const smsEngineRunsDetailSearchSchema = smsEngineRunsSearchSchema.extend(
    {
        from_list: z.boolean().optional(),
    },
);

export type SmsEngineRunsDetailSearch = z.infer<
    typeof smsEngineRunsDetailSearchSchema
>;

/** Strip the detail-only `from_list` flag back down to list search params, for
 * returning to the list with the same filters intact. */
export function smsDetailSearchToListSearch(
    search: SmsEngineRunsDetailSearch,
): SmsEngineRunsSearch {
    const { from_list: _fromList, ...listSearch } = search;
    return listSearch;
}
